import React from 'react'
import { AlertTriangle, FlaskConical } from 'lucide-react'
import { Button, Modal } from '../../../components/ui'

interface CardEditConfirmDialogProps {
  isOpen: boolean
  platform: 'Wildberries' | 'Ozon' | 'Яндекс Маркет' | (string & {})
  cabinet: string
  title: string
  changedFields: string[]
  violations?: string[]
  warning?: string
  dryRun: boolean
  /**
   * Свой текст вместо стандартного: окно карточки площадки сообщает, что правка
   * уходит через журнал правок, и отдельно говорит, если отправка выключена.
   */
  notice?: React.ReactNode
  /** Подпись кнопки подтверждения; по умолчанию «Подтвердить». */
  confirmLabel?: string
  saving: boolean
  onClose: () => void
  onConfirm: () => void
}

export const CardEditConfirmDialog: React.FC<CardEditConfirmDialogProps> = ({
  isOpen,
  platform,
  cabinet,
  title,
  changedFields,
  violations = [],
  warning,
  dryRun,
  notice,
  confirmLabel = 'Подтвердить',
  saving,
  onClose,
  onConfirm,
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    subtitle={`${platform} · кабинет «${cabinet}»`}
    layer="nested"
    footer={
      <>
        <Button variant="secondary" onClick={onClose}>
          Отмена
        </Button>
        <Button loading={saving} disabled={violations.length > 0} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </>
    }
  >
    <div className="space-y-3 text-sm">
      {notice ? (
        <div
          className={
            dryRun
              ? 'rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-xs text-indigo-800 dark:border-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-200'
              : 'rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
          }
        >
          {notice}
        </div>
      ) : dryRun ? (
        <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-indigo-800 dark:border-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-200">
          <div className="flex items-center gap-2 font-medium">
            <FlaskConical className="h-4 w-4" />
            Черновой режим
          </div>
          <p className="mt-1 text-xs">
            Наружу ничего не уйдёт: изменения только показываются в сформированном запросе.
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          После подтверждения запрос будет отправлен в seller API площадки.
        </div>
      )}
      {warning && (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{warning}</span>
        </div>
      )}
      <div>
        <div className="text-xs font-medium text-gray-600 dark:text-zinc-300">Изменённые поля</div>
        <ul className="mt-1 list-inside list-disc text-xs text-gray-700 dark:text-zinc-300">
          {changedFields.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </div>
      {violations.length > 0 && (
        <div className="rounded-md bg-red-50 px-3 py-2.5 text-red-800 dark:bg-red-950/35 dark:text-red-300">
          <div className="text-xs font-semibold">Сохранение заблокировано</div>
          <ul className="mt-1 list-inside list-disc text-xs">
            {violations.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </Modal>
)

export default CardEditConfirmDialog
